import Box from '@mui/material/Box'

import {
  COMPASS_DIRECTIONS,
  DEGREE_MARKS,
  isCardinal,
  toCompassDegrees,
  toRotateLabel
} from './compass-directions'
import {
  CARDINAL_LABEL_SX,
  COLLAPSED_DEGREES_SX,
  COLLAPSED_DIAL,
  COLLAPSED_NORTH_SX,
  createDialSx,
  DEGREE_MARK_SX,
  EXPANDED_DEGREES_SX,
  EXPANDED_DIAL,
  FIXED_LAYER_SX,
  INTERCARDINAL_LABEL_SX,
  LAYER_SX,
  TICK_STEP_DEGREES
} from './CompassDial.styles'

// `new URL(..., import.meta.url)` resolves under both vite and the webpack preprocessor that Cypress e2e uses.
// A default `.svg` import has no loader under webpack, so any e2e spec that reaches `@shared` would fail to bundle.
const gaugeRingUrl = new URL('./assets/gauge-ring.svg', import.meta.url).href
const haloBackgroundUrl = new URL('./assets/halo-background.svg', import.meta.url).href
const highlightEllipseUrl = new URL('./assets/highlight-ellipse.svg', import.meta.url).href
const innerDiscUrl = new URL('./assets/inner-disc.svg', import.meta.url).href
const northPointerUrl = new URL('./assets/north-pointer.svg', import.meta.url).href
const topPointerUrl = new URL('./assets/top-pointer.svg', import.meta.url).href

const COLLAPSED_SX = createDialSx(COLLAPSED_DIAL)
const EXPANDED_SX = createDialSx(EXPANDED_DIAL)

const TICK_BEARINGS = Array.from(
  { length: 360 / TICK_STEP_DEGREES },
  (_, index) => index * TICK_STEP_DEGREES
)

export type CompassDialProps =
  | {
      readonly variant: 'collapsed'
      /** The view bearing in degrees. Mapbox's -180 to 180 is accepted. */
      readonly bearing: number
      readonly testIdPrefix: string
    }
  | {
      readonly variant: 'expanded'
      readonly bearing: number
      readonly testIdPrefix: string
      /** Undefined until the view loads, which leaves the labels inert. */
      readonly onBearingSelect: ((bearing: number) => void) | undefined
    }

const DegreesReadout = ({
  bearing,
  testIdPrefix,
  sx
}: {
  readonly bearing: number
  readonly testIdPrefix: string
  readonly sx: typeof EXPANDED_DEGREES_SX | typeof COLLAPSED_DEGREES_SX
}) => (
  <Box data-testid={`${testIdPrefix}-compass-degrees`} sx={sx}>
    {`${toCompassDegrees(bearing)}°`}
  </Box>
)

/** The small dial on the collapsed compass button: halo, ring, north and degrees. */
const CollapsedDial = ({
  bearing,
  testIdPrefix
}: {
  readonly bearing: number
  readonly testIdPrefix: string
}) => (
  <Box sx={COLLAPSED_SX.frame}>
    <Box sx={{ ...LAYER_SX, transform: `rotate(${-bearing}deg)` }}>
      <Box component="img" src={haloBackgroundUrl} alt="" sx={COLLAPSED_SX.halo} />
      <Box component="img" src={gaugeRingUrl} alt="" sx={COLLAPSED_SX.gaugeRing} />
      <Box sx={{ ...COLLAPSED_NORTH_SX, ...COLLAPSED_SX.placeLabel(0, bearing) }}>
        N
      </Box>
    </Box>
    <Box sx={FIXED_LAYER_SX}>
      <Box component="img" src={innerDiscUrl} alt="" sx={COLLAPSED_SX.innerDisc} />
      <DegreesReadout
        bearing={bearing}
        testIdPrefix={testIdPrefix}
        sx={COLLAPSED_DEGREES_SX}
      />
    </Box>
  </Box>
)

/** The full Figma dial: degree marks, ticks, and a button per direction. */
const ExpandedDial = ({
  bearing,
  testIdPrefix,
  onBearingSelect
}: {
  readonly bearing: number
  readonly testIdPrefix: string
  readonly onBearingSelect: ((bearing: number) => void) | undefined
}) => (
  <Box sx={EXPANDED_SX.frame}>
    <Box component="img" src={topPointerUrl} alt="" sx={EXPANDED_SX.topPointer} />

    <Box sx={{ ...LAYER_SX, transform: `rotate(${-bearing}deg)` }}>
      <Box component="img" src={haloBackgroundUrl} alt="" sx={EXPANDED_SX.halo} />
      <Box component="img" src={gaugeRingUrl} alt="" sx={EXPANDED_SX.gaugeRing} />
      {TICK_BEARINGS.map((tickBearing) => (
        <Box
          key={tickBearing}
          sx={{ ...EXPANDED_SX.tick, transform: `rotate(${tickBearing}deg)` }}
        />
      ))}
      <Box
        component="img"
        src={northPointerUrl}
        alt=""
        sx={EXPANDED_SX.northPointer}
      />
      {DEGREE_MARKS.map((mark) => (
        <Box
          key={mark}
          aria-hidden
          sx={{ ...DEGREE_MARK_SX, ...EXPANDED_SX.placeDegreeMark(mark, bearing) }}
        >
          {mark}
        </Box>
      ))}
      {COMPASS_DIRECTIONS.map((direction) => (
        <Box
          key={direction.label}
          component="button"
          type="button"
          aria-label={toRotateLabel(direction)}
          data-testid={`${testIdPrefix}-compass-direction-${direction.label}`}
          disabled={onBearingSelect === undefined}
          onClick={() => onBearingSelect?.(direction.bearing)}
          sx={{
            ...(isCardinal(direction)
              ? CARDINAL_LABEL_SX
              : INTERCARDINAL_LABEL_SX),
            ...EXPANDED_SX.placeLabel(direction.bearing, bearing)
          }}
        >
          {direction.label}
        </Box>
      ))}
    </Box>

    <Box sx={FIXED_LAYER_SX}>
      <Box component="img" src={innerDiscUrl} alt="" sx={EXPANDED_SX.innerDisc} />
      <Box
        component="img"
        src={highlightEllipseUrl}
        alt=""
        sx={EXPANDED_SX.highlight}
      />
      <DegreesReadout
        bearing={bearing}
        testIdPrefix={testIdPrefix}
        sx={EXPANDED_DEGREES_SX}
      />
    </Box>
  </Box>
)

/**
 * The compass dial from Figma. It turns with the view under a fixed pointer
 * and shows the bearing in its centre.
 */
export const CompassDial = (props: CompassDialProps) =>
  props.variant === 'collapsed' ? (
    <CollapsedDial
      bearing={props.bearing}
      testIdPrefix={props.testIdPrefix}
    />
  ) : (
    <ExpandedDial
      bearing={props.bearing}
      testIdPrefix={props.testIdPrefix}
      onBearingSelect={props.onBearingSelect}
    />
  )
